import { ACTION_MS, GEAR, rotationMs } from './config.js';
import { beats, counterTo, type Item } from './items.js';
import type { Action, AgentId, Match } from './match.js';
import { nextActionToward } from './orientation.js';
import type { Rng } from './rng.js';
import { coreBand, type CoreBand } from './sensors.js';
import { DIRECTIONS, type Vec3 } from './vec.js';

export type BotTier = 'novice' | 'tactical' | 'relentless';

export interface BotTierConfig {
  /** Pause after each action finishes before the bot acts again (ms, min–max). */
  reactionMs: readonly [number, number];
  /** Chance per decision of a random move (which can mean bonking a wall). */
  errorRate: number;
  /** Swaps to counter you when its threat sensor reveals your item. */
  swaps: boolean;
  /** Chases you on sight once it knows it holds the winning item. */
  chases: boolean;
  /** Chance it uses each core-sensor reading to narrow its search. */
  readingUse: number;
}

export const BOT_TIERS: Record<BotTier, BotTierConfig> = {
  novice: { reactionMs: [1200, 2000], errorRate: 0.1, swaps: false, chases: false, readingUse: 0.5 },
  tactical: { reactionMs: [450, 850], errorRate: 0, swaps: true, chases: false, readingUse: 1 },
  relentless: { reactionMs: [200, 400], errorRate: 0, swaps: true, chases: true, readingUse: 1 },
};

/** Minimum time between bot swaps, so it can't flicker items. */
const SWAP_COOLDOWN_MS = 3500;

/**
 * A bot that plays by the player's rules. It knows only what a player would:
 * the doors of rooms it has stood in, its own core and threat sensor bands, and what it
 * can see through open doorways. It never reads the core's room or the rival's item.
 */
export class Bot {
  readonly id: AgentId;
  readonly tier: BotTier;
  private readonly config: BotTierConfig;
  private readonly rng: Rng;
  private readonly seen = new Set<number>();
  private candidates: number[] = [];
  private target = -1;
  private lastRoom = -1;
  private readyAt = 0;
  private lastSwapAt = Number.NEGATIVE_INFINITY;
  /** The rival's item as last revealed by this bot's threat sensor. */
  private knownRivalItem: Item | null = null;

  constructor(id: AgentId, tier: BotTier, rng: Rng, startAt = 0) {
    this.id = id;
    this.tier = tier;
    this.config = BOT_TIERS[tier];
    this.rng = rng;
    this.readyAt = startAt;
  }

  /**
   * Decide the next action, or null to wait. Call after `match.advance(now)`.
   * The caller submits the action with `match.input`.
   */
  think(match: Match, now: number): Action | null {
    const self = match.agent(this.id);
    if (!self || match.over || self.active || now < this.readyAt) return null;
    const maze = match.maze;

    const view = match.viewFor(this.id);
    const sensors = view.sensors;
    if (self.room !== this.lastRoom) this.observe(match, self.room, sensors.jammed);

    // Fleeing: just came through a door with a threat close behind, so slam it.
    if (
      this.config.swaps &&
      self.justPassed &&
      self.gear.bulkhead > 0 &&
      (sensors.threat === 'near' || sensors.threat === 'danger')
    ) {
      return this.commit({ kind: 'bulkhead' }, now, ACTION_MS.useGear);
    }

    // Threatened: the sensor reveals the rival's item (the one that beats ours), so counter it.
    if (this.config.swaps && sensors.threat !== 'none' && now - this.lastSwapAt > SWAP_COOLDOWN_MS) {
      const theirs = counterTo(self.item);
      this.knownRivalItem = theirs;
      this.lastSwapAt = now;
      return this.commit({ kind: 'swap', item: counterTo(theirs) }, now, ACTION_MS.swap);
    }

    const hunting = this.config.chases && this.knownRivalItem !== null && beats(self.item, this.knownRivalItem);
    const rival = view.rival;

    // Hunting and close: blind their sensors so they can't feel it coming.
    if (hunting && rival && self.gear.emp > 0 && maze.straightDistance(self.room, rival.room) <= GEAR.empRadius) {
      return this.commit({ kind: 'emp' }, now, ACTION_MS.useGear);
    }

    let dir: Vec3 | null = null;
    if (this.config.errorRate > 0 && this.rng() < this.config.errorRate) {
      dir = DIRECTIONS[Math.floor(this.rng() * 6)]!;
    } else {
      if (hunting && rival) dir = maze.firstStepToward(self.room, rival.room); // it can see you down the doorways
      else if (hunting && view.scorch?.exit) dir = view.scorch.exit; // fresh scorch shows which way you went
      dir ??= this.nextDirection(match, self.room, now);
    }
    if (!dir) return null;

    const next = nextActionToward(self.orientation, dir);
    if (next === 'step') {
      const ahead = maze.neighbor(self.room, self.orientation.facing);
      const duration = ahead >= 0 && match.isPassable(self.room, ahead, now) ? ACTION_MS.step : ACTION_MS.bonk;
      return this.commit({ kind: 'step' }, now, duration);
    }
    return this.commit({ kind: 'rotate', rotation: next }, now, rotationMs(next));
  }

  private commit(action: Action, now: number, duration: number): Action {
    const [lo, hi] = this.config.reactionMs;
    this.readyAt = now + duration + lo + this.rng() * (hi - lo);
    return action;
  }

  /** Record a new room and narrow the core's possible rooms using this reading. */
  private observe(match: Match, room: number, jammed: boolean): void {
    const maze = match.maze;
    this.lastRoom = room;
    this.seen.add(room);
    if (this.candidates.length === 0) for (let r = 0; r < maze.roomCount; r++) this.candidates.push(r);
    if (jammed) {
      // Static tells it nothing about the core; just rule out the room it's standing in.
      this.candidates = this.candidates.filter((c) => c !== room);
      if (this.target === room || this.target < 0) this.target = this.candidates[0] ?? room;
      return;
    }
    const band: CoreBand = match.sensorsFor(this.id).core;
    const useReading = this.rng() < this.config.readingUse;
    this.candidates = this.candidates.filter(
      (c) => !this.seen.has(c) && (!useReading || coreBand(maze.straightDistance(room, c)) === band),
    );
    if (this.candidates.length === 0) for (let r = 0; r < maze.roomCount; r++) if (!this.seen.has(r)) this.candidates.push(r);

    if (band === 'none') {
      // No signal: commit to a distant sweep target and keep it until it's ruled out.
      if (!this.candidates.includes(this.target)) {
        const far = this.candidates.filter((c) => {
          const d = maze.straightDistance(room, c);
          return d >= 5 && d <= 9;
        });
        const pool = far.length > 0 ? far : this.candidates;
        this.target = pool[Math.floor(this.rng() * pool.length)]!;
      }
      return;
    }
    // Signal: home in on the nearest room that still fits every reading so far.
    let best = this.candidates[0]!;
    let bestDistance = Infinity;
    for (const c of this.candidates) {
      const d = maze.straightDistance(room, c);
      if (d < bestDistance) {
        bestDistance = d;
        best = c;
      }
    }
    this.target = best;
  }

  /**
   * Plan over the known part of the maze only: head for the known room (visited, or a
   * doorway seen from a visited room) that best trades path length against closeness to the target.
   */
  private nextDirection(match: Match, room: number, now: number): Vec3 | null {
    const maze = match.maze;
    const dist = new Map<number, number>([[room, 0]]);
    const prev = new Map<number, number>();
    const queue = [room];
    let best = -1;
    let bestScore = Infinity;
    for (let head = 0; head < queue.length; head++) {
      const cur = queue[head]!;
      const d = dist.get(cur)!;
      if (cur !== room && (!this.seen.has(cur) || cur === this.target)) {
        const score = d + 2.2 * maze.straightDistance(cur, this.target);
        if (score < bestScore) {
          bestScore = score;
          best = cur;
        }
      }
      if (!this.seen.has(cur)) continue; // doors beyond unvisited rooms are unknown
      for (const dir of DIRECTIONS) {
        const next = maze.through(cur, dir);
        // It can see a sealed door in its own room and routes around it.
        if (next >= 0 && cur === room && !match.isPassable(cur, next, now)) continue;
        if (next >= 0 && !dist.has(next)) {
          dist.set(next, d + 1);
          prev.set(next, cur);
          queue.push(next);
        }
      }
    }
    if (best < 0) return null;
    let step = best;
    while (prev.get(step) !== room) step = prev.get(step)!;
    return maze.directionBetween(room, step);
  }
}
